/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Cookies from 'universal-cookie';
import { COOKIE_DOMAIN } from '@helpers/utils';
import IconEmail from '@hiredigital/ui/Icon/icons/at-symbol.inline.svg';
import IconProfile from '@hiredigital/ui/Icon/icons/user.inline.svg';
import IconPhone from '@hiredigital/ui/Icon/icons/phone-line.inline.svg';
import PhoneNumber from '@hiredigital/ui/PhoneNumber';
import { getCountries, getTalentCounts } from '@apis/common';
import { postProspect } from '@apis/prospects';
import BaseLayout from '@components/Layout/BaseLayout';

import Button from '@hiredigital/ui/Button';

import TextInput from '@hiredigital/ui/Input/SimpleTextInput';

import { useCommon } from '@context/common';
import { validateEmail, validateCorporateEmail, identifyHubspotUser } from '@helpers/forms';
import { baseSkills } from '@helpers/onboardingUtils';
import MainContainer from '@components/PageComponents/Onboarding/Container';
import LeftArea from '@components/PageComponents/Onboarding/LeftArea';
import RightArea from '@components/PageComponents/Onboarding/RightArea';
import Styles from './GetStarted.module.scss';
import { useRouter } from 'next/router';
import { formatNumber } from '@hiredigital/lib/helpers/utils';
import { submitHubspotForm } from '@components/Registration/common/helpers';
import { sendEvent } from '@lib/gtm.js';

import PillSelector from '@hiredigital/ui/PillSelector';

const LogoSection = dynamic(() => import('@components/PageComponents/LogoSection/LogoSection'));

const OnboardingPage = () => {
  const cookies = new Cookies();
  const router = useRouter();
  const common = useCommon();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountry, setPhoneCountry] = useState({});
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState({});
  const [skillList, setSkillList] = useState(baseSkills);
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    const loadTalentCounts = async () => {
      const { data } = await getTalentCounts();
      mapTalentCount(data.results);
    };
    loadTalentCounts();
  }, []);

  const mapTalentCount = (talentCounts) => {
    setSkillList(
      skillList.map((skill) => {
        const count = talentCounts.find((t) => t.id === skill.id)?.count || 0;
        return { ...skill, count };
      })
    );
  };

  const triggerHubspot = async () => {
    try {
      const pageUrl = window.location.href;
      const pageName = document.title;

      identifyHubspotUser({
        email,
        name: name,
        phone_number: `${phoneCountry?.dialCode} ${phoneNumber}`,
        type: 1,
      });
      sendEvent({ event: 'GTM-EntLeadGen' });

      const hubspotData = {
        ...router.query,
        fullName: name,
        phone: `${phoneCountry?.dialCode} ${phoneNumber}`,
        email,
        work_email: email,
      };

      await submitHubspotForm(
        process.env.NEXT_PUBLIC_ENTERPRISE_FORMID,
        pageUrl,
        pageName,
        hubspotData
      );
      sendEvent({ event: 'GTM-EntLeadGenConfirm' });
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = (event) => {
    setLoading(true);
    if (validateData()) {
      const user = {
        name: name,
        phoneNumber,
        email: email,
        phoneCountry,
      };
      triggerHubspot();
      postProspect(user)
        .then((response) => {
          const selectedId = skillList.filter((_, idx) => selected.includes(idx)).map((v) => v.id);
          cookies.set('skills', selectedId, COOKIE_DOMAIN);
          router.push(`/get-started/${response.data?.uuid}/requirements`);
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        })
        .catch((error) => {
          setTimeout(() => {
            setLoading(false);
          }, 1000);
        });
    } else {
      // Handle case when not valid
      setLoading(false);
    }
  };

  const validateData = () => {
    let err = { ...error };
    if (email === '') {
      err.email = 'Enter Work Email Address';
      setError((prevState) => ({ ...prevState, email: 'Enter Work Email Address' }));
    }
    if (!validateCorporateEmail(email)) {
      err.email = 'Invalid Work Email Address';
      setError((prevState) => ({ ...prevState, email: 'Invalid Work Email Address' }));
    }
    if (phoneNumber === '') {
      err.phoneNumber = 'Enter Phone Number';
      setError((prevState) => ({ ...prevState, phoneNumber: 'Enter Phone Number' }));
    }
    if (name === '') {
      err.name = 'Enter your name';
      setError((prevState) => ({ ...prevState, name: 'Enter your name' }));
    }
    if (err === {} || (err.name === '' && err.phoneNumber === '' && err.email === '')) {
      return true;
    }
    // if (error === {} || (error?.name === '' && error?.phoneNumber === '' && error?.email === ''))
    //   return true;
    else return false;
  };

  const handleEmailChange = (e) => {
    setError((prevState) => ({ ...prevState, email: '' }));
    setEmail(e.target.value);
  };

  const handleNameChange = (e) => {
    setError((prevState) => ({ ...prevState, name: '' }));
    setName(e.target.value);
  };

  const handlePhoneCountryChange = (value) => {
    setPhoneCountry(value);
  };

  const handlePhoneNumberChange = (value) => {
    setError((prevState) => ({ ...prevState, phoneNumber: '' }));
    setPhoneNumber(value);
  };

  useEffect(() => {
    setPhoneCountry(common?.systemCountry);
  }, [common]);

  const handleEnterKeyPress = (e) => {
    if (e.which === 13) {
      handleSubmit();
    }
  };

  const handleSelectTalentTypes = (_, idx) => {
    if (selected.includes(idx)) {
      setSelected(selected.filter((v) => v !== idx));
    } else {
      setSelected([...selected, idx]);
    }
  };

  // const totalTalentCount = selected.reduce(
  //   (accumulator, current) => accumulator + (skillList?.[current]?.count || 0),
  //   0
  // );
  // const talentLabel = selected?.length === 1 ? skillList[selected[0]].label : 'Talents';
  // const formattedCount = formatNumber(totalTalentCount, 0);
  // const subheadline =
  //   selected?.length > 0
  //     ? `We have more than ${formattedCount} ${talentLabel} that might suit your needs`
  //     : '';

  return (
    <BaseLayout
      pageHead={{
        title: `Registration | Hire Digital`,
      }}>
      <MainContainer>
        <LeftArea
          title={`The Best Talent On Demand`}
          description={`Work with the highest performing talent on your most important projects.`}>
          <LogoSection />
        </LeftArea>
        <RightArea activeNavIndex={0} hasNav={true} headline={`What are your talent needs?`}>
          <PillSelector
            items={skillList}
            selected={selected}
            onSelect={handleSelectTalentTypes}
            containerProps={{ style: { marginBottom: '10px' } }}
          />
          <div>
            <div className={Styles.headline}>{`Your contact info`}</div>
          </div>
          <div style={{ marginBottom: '30px' }}>
            <div className={Styles.inputContainer}>
              <TextInput
                name='name'
                label='Name'
                icon={IconProfile}
                value={name}
                error={error?.name || ''}
                required={true}
                onKeyPress={handleEnterKeyPress}
                onChange={(e) => handleNameChange(e)}
              />
            </div>
            <div className={Styles.inputContainer}>
              <PhoneNumber
                icon={IconPhone}
                phoneCountry={phoneCountry}
                phoneNumber={phoneNumber}
                onPhoneCountryChange={handlePhoneCountryChange}
                onPhoneNumberChange={handlePhoneNumberChange}
                getCountries={getCountries}
                error={error?.phoneNumber || ''}
                shouldValidate={false}
                required={true}
              />
            </div>
            <div className={Styles.inputContainer}>
              {/*<IconEmail className={Styles.icon} />*/}
              <TextInput
                name='email'
                label='Work Email Address'
                icon={IconEmail}
                error={error.email || ''}
                value={email}
                required={true}
                onKeyPress={handleEnterKeyPress}
                onChange={(e) => handleEmailChange(e)}
              />
            </div>
            <div className={Styles.actionRow}>
              <Button
                className={Styles.loginButton}
                name='submit'
                type={Button.Type.DARKBLUE}
                size={Button.Size.FULLWIDTH}
                onClick={handleSubmit}
                isLoading={loading}>
                {`Submit your request`}
              </Button>
            </div>
          </div>

          <RightArea.Footer hasAgreement />
        </RightArea>
      </MainContainer>
    </BaseLayout>
  );
};

export default OnboardingPage;
